import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import BrandLogo from '@/components/BrandLogo';
import { 
  Monitor, LogIn, CheckCircle, ArrowRight, Users, Activity, 
  TrendingUp, Shield, Award, BookOpen, BarChart3, 
  ChevronDown, ChevronUp, Menu, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileVideoUrl, setMobileVideoUrl] = useState('/videos/exlva.mp4');
  const defaultFaqs = [
    {
      faq_id: 'default_faq_1',
      question: 'What services does the platform offer?',
      answer: 'Our platform provides AI-powered solutions tailored for hospital training management and patient care enhancement, including comprehensive training management systems, real-time analytics, and compliance tracking.'
    },
    {
      faq_id: 'default_faq_2',
      question: 'What is HTMS?',
      answer: 'HTMS stands for Hospital Training Management System, an innovative AI-driven learning management system designed to manage and optimize training for healthcare staff with real-time support and performance tracking.'
    },
    {
      faq_id: 'default_faq_3',
      question: 'Is the system user-friendly?',
      answer: 'Absolutely! Our platform is designed with intuitive navigation ensuring both staff and administrators can effortlessly access training modules, analytics dashboards, and reports across all devices.'
    },
    {
      faq_id: 'default_faq_4',
      question: 'How does the platform leverage AI?',
      answer: 'The platform uses advanced AI algorithms to interpret individual staff queries, deliver personalized learning paths based on each user\'s role and performance metrics, ensuring targeted knowledge delivery.'
    },
    {
      faq_id: 'default_faq_5',
      question: 'Is there a demo available?',
      answer: 'Yes! We offer a comprehensive demo of the platform. Please fill out the demo request form, and our team will contact you promptly to schedule a session.'
    },
    {
      faq_id: 'default_faq_6',
      question: 'How secure is the system?',
      answer: 'Security is our priority. We implement robust encryption for all data during storage and transmission, along with multi-tenant access controls that comply with industry standards.'
    }
  ];
  const [faqs, setFaqs] = useState(defaultFaqs);

  // Fetch signed URL for mobile video
  useEffect(() => {
    const fetchMobileVideoUrl = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/public/mobile-video-url`);
        if (response.ok) {
          const data = await response.json();
          if (data.url) {
            setMobileVideoUrl(data.url);
          }
        }
      } catch (error) {
        console.error('Failed to fetch mobile video URL:', error);
      }
    };
    fetchMobileVideoUrl();
  }, []);

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/cms/faqs`);
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        if (Array.isArray(data.faqs) && data.faqs.length > 0) {
          setFaqs(data.faqs);
        }
      } catch (error) {
        console.error('Failed to fetch CMS FAQs:', error);
      }
    };
    fetchFaqs();
  }, []);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const steps = [
    {
      number: 1,
      title: "Click & get started",
      description: "Hit the 'Sign up' button to begin your journey with our platform.",
      image: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=400&h=300&fit=crop"
    },
    {
      number: 2,
      title: "Fill in your details",
      description: "Enter your Hospital name, Email address, and Phone number to register.",
      image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=300&fit=crop"
    },
    {
      number: 3,
      title: "Check your inbox",
      description: "Receive your login credentials instantly via email.",
      image: "https://images.unsplash.com/photo-1557200134-90327ee9fafa?w=400&h=300&fit=crop"
    },
    {
      number: 4,
      title: "Sign in & explore",
      description: "Use the credentials to log in and access your dashboard!",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header/Navigation */}
      <header className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <BrandLogo />
            </div>

            {/* Desktop Navigation & Buttons - All aligned right with equal spacing */}
            <div className="hidden md:flex items-center space-x-6">
              <button onClick={() => navigate('/')} className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-medium transition-colors">
                Home
              </button>
              <button onClick={() => navigate('/solutions')} className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-medium transition-colors">
                The solution
              </button>
              <button onClick={() => navigate('/pricing')} className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-medium transition-colors">
                Pricing
              </button>
              <button onClick={() => navigate('/contact')} className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-medium transition-colors">
                Contact us
              </button>
              <Button 
                onClick={() => navigate('/request-demo')}
                className="bg-[#10B981] hover:bg-[#059669] text-white text-sm px-4 py-2 rounded-lg transition-colors"
              >
                <Monitor className="w-4 h-4 mr-2" />
                Request for demo
              </Button>
              <Button 
                onClick={() => navigate('/signin')}
                className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white text-sm px-4 py-2 rounded-lg transition-colors"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Sign in
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-gray-700"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
              <nav className="flex flex-col space-y-3">
                <button onClick={() => navigate('/')} className="text-sm text-gray-700 hover:text-[#0056B3] font-medium text-left">
                  Home
                </button>
                <button onClick={() => navigate('/solutions')} className="text-sm text-gray-700 hover:text-[#0056B3] font-medium text-left">
                  The solution
                </button>
                <button onClick={() => navigate('/pricing')} className="text-sm text-gray-700 hover:text-[#0056B3] font-medium text-left">
                  Pricing
                </button>
                <button onClick={() => navigate('/contact')} className="text-sm text-gray-700 hover:text-[#0056B3] font-medium text-left">
                  Contact us
                </button>
                <Button 
                  onClick={() => navigate('/request-demo')}
                  className="bg-[#10B981] hover:bg-[#059669] text-white text-sm w-full justify-center"
                >
                  Request for demo
                </Button>
                <Button 
                  onClick={() => navigate('/signin')}
                  className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white text-sm w-full justify-center"
                >
                  Sign in
                </Button>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Full Screen Video Banner */}
      <section className="relative w-full overflow-hidden bg-gray-900" style={{ marginTop: '72px' }}>
        {/* Desktop Video */}
        <video 
          autoPlay 
          muted 
          loop 
          playsInline
          preload="auto"
          className="hidden md:block w-full h-auto"
          style={{ minHeight: 'calc(100vh - 72px)' }}
        >
          <source src="/videos/exlva.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        {/* Mobile Video - uses signed S3 URL */}
        <video 
          autoPlay 
          muted 
          loop 
          playsInline
          preload="auto"
          className="md:hidden w-full h-auto"
          key={mobileVideoUrl}
        >
          <source src={mobileVideoUrl} type="video/webm" />
          <source src="/videos/exlva.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </section>

      {/* Hero Subchapter - Below Video */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-8 leading-tight">
            The AI-powered hospital training management system
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-6 leading-relaxed max-w-4xl mx-auto">
            Transform hospital training with compliance tracking, smart analytics, and real-time reporting to enhance patient care and outcomes.
          </p>
          <p className="text-base md:text-lg text-gray-600 mb-10 max-w-3xl mx-auto">
            Powered by AI, this intelligent system revolutionizes hospital compliance training with an intuitive, engaging approach that optimizes medical practice and drives operational excellence.
          </p>
          <Button 
            onClick={() => navigate('/signup')}
            className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white text-lg px-10 py-6 rounded-lg"
          >
            Get started
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* CTA Subchapter */}
      <section className="py-20 px-6 bg-gradient-to-br from-[#0056B3] to-[#00AEEF] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Get started today</h2>
          <p className="text-xl mb-10 opacity-90">
            Join us today to streamline your hospital training management system and boost your data analytics, reporting, and research capabilities.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              onClick={() => navigate('/signup')}
              className="bg-white text-[#0056B3] hover:bg-gray-100 text-lg px-8 py-6 rounded-lg"
            >
              Sign up
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              onClick={() => navigate('/request-demo')}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6 rounded-lg"
            >
              <Monitor className="w-5 h-5 mr-2" />
              Request for demo
            </Button>
          </div>
        </div>
      </section>

      {/* Step-by-Step Guide */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-4">
            Your step-by-step guide to getting started
          </h2>
          <p className="text-center text-gray-600 mb-16 text-lg">Follow these simple steps to begin your journey</p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={step.image} 
                    alt={step.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4 w-12 h-12 bg-[#0056B3] text-white rounded-full flex items-center justify-center text-xl font-bold">
                    {step.number}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    Step {step.number}: {step.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">FAQs</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={faq.faq_id || index}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-[#0056B3]" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4 text-gray-600 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default HomePage;
